import styles from '@/storybook/components/FormCollection/Input.module.css';
import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import DropdownDivider from '@/storybook/components/FormCollection/components/DropdownDivider/DropdownDivider';
import DropdownItemLabelWithRatingStars from '@/storybook/components/FormCollection/components/DropdownItemLabelWithRatingsStars/DropdownItemLabelWithRatingsStars';
import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import useVirtualizedOptions, {
  VirtualRowMeta,
} from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getSelectItemLabelContent,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useCallback, useMemo } from 'react';

type UseMultiLevelSelectVirtualizationParams<T> = {
  selectedOptions: SelectItem<T>[];
  notSelectedOptions: SelectItem<T>[];
  categoriesToDisplay: Array<{ category: string; index: number }>;
  noCategoryLabel: string;
  value: SelectItem<T>[];
  isOptionSelected: (option: SelectItem<T>) => boolean;
  handleClickOnCheckbox: (option: SelectItem<T>) => void;
  setSelectedOption: (option: SelectItem<T> | undefined) => void;
};

export function useMultiLevelSelectVirtualization<T>({
  selectedOptions,
  notSelectedOptions,
  categoriesToDisplay,
  noCategoryLabel,
  value,
  isOptionSelected,
  handleClickOnCheckbox,
  setSelectedOption,
}: UseMultiLevelSelectVirtualizationParams<T>) {
  const needsDivider = selectedOptions.length > 0 && notSelectedOptions.length > 0;

  const hasCategorizedOptions = useMemo(
    () => notSelectedOptions.some(opt => opt?.category != null),
    [notSelectedOptions],
  );

  const { virtualizedList, parentRef } = useVirtualizedOptions<T>({
    selectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider: needsDivider,
    noCategoryLabel,
  });

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>, virtualIndex?: number) => {
      if (vMeta.kind === 'divider') {
        return <DropdownDivider key={`divider-${virtualIndex}`} />;
      }
      if (vMeta.kind === 'category' && vMeta.category) {
        return (
          <ListHeader
            key={`category-${vMeta.category}`}
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
          />
        );
      }
      if ((vMeta.kind === 'selected' || vMeta.kind === 'notSelected') && vMeta.option) {
        const option = vMeta.option;
        const isSelectedRow = vMeta.kind === 'selected';
        const primaryLabel = extractSelectItemLabelText(option);
        const labelContent = isSelectedRow ? (
          <DropdownItemLabelWithRatingStars option={option} value={value} onEdit={setSelectedOption} />
        ) : (
          getSelectItemLabelContent(option)
        );
        const rowPrefix = isSelectedRow ? 'sortedSelectedOption' : 'sortedNotSelectedOption';
        const stableKey = createSelectItemKey(option);
        const checkboxTestId = `${rowPrefix}-${stableKey}`;
        const checked = isOptionSelected(option);
        return (
          <TreeItem
            key={stableKey}
            className={!isSelectedRow ? styles.noSelect : undefined}
            label={primaryLabel}
            labelContent={labelContent}
            tooltipText={primaryLabel}
            variant="flatList"
            level={0}
            showCheckbox
            checked={checked}
            onCheck={() => handleClickOnCheckbox(option)}
            isSelected={checked}
            data-testid={`${rowPrefix}Row-${stableKey}`}
            checkboxDataTestId={checkboxTestId}
          />
        );
      }
      return null;
    },
    [value, isOptionSelected, handleClickOnCheckbox, setSelectedOption],
  );

  return {
    virtualizedList,
    parentRef,
    renderVirtualRow,
  };
}
